import { useState } from "react";
import { motion } from "framer-motion";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { CONTACT } from "@/constants/testIds";
import { Mail, MapPin, Instagram, ArrowUpRight, Send } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const buildMailto = (e) => {
    e.preventDefault();
    const subject = `Επικοινωνία από ${form.name || "άγνωστο"} — blouzes4all.gr`;
    const body = [
      `Όνομα: ${form.name}`,
      `Email: ${form.email}`,
      ``,
      `Μήνυμα:`,
      form.message,
    ].join("\n");
    window.location.href = `mailto:medhealthgr@gmail.com?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="grain">
      <Nav />
      <main
        id="main"
        data-testid={CONTACT.root}
        className="pt-28 md:pt-36 px-4 md:px-8 lg:px-12 pb-24"
      >
        <div className="flex items-baseline justify-between mb-10 md:mb-16">
          <span className="font-mono-caps text-xs">§ 04 — ΕΠΙΚΟΙΝΩΝΙΑ</span>
          <span className="font-mono-caps text-xs">medhealthgr@gmail.com</span>
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="font-display text-6xl sm:text-7xl md:text-[13vw] lg:text-[11vw] leading-[0.85] mb-16"
        >
          ΓΡΑΨΕ<br />
          <span className="text-outline">ΜΑΣ</span>
          <span className="text-[var(--pink)]">.</span>
        </motion.h1>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <motion.aside
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="md:col-span-5"
          >
            <p className="text-base md:text-lg leading-relaxed max-w-md text-[var(--ink-70)] mb-10">
              Έχεις custom ιδέα, θες συνεργασία ή απλά μια ερώτηση για μέγεθος;
              Στείλε μας μήνυμα και θα σου απαντήσουμε άμεσα από το inbox μας.
            </p>

            <ul className="space-y-6">
              <li className="flex items-start gap-4">
                <div className="w-10 h-10 border border-[var(--ink)] flex items-center justify-center bg-[var(--lime)]">
                  <Mail className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-mono-caps text-[10px] opacity-60 mb-1">EMAIL</div>
                  <a
                    href="mailto:medhealthgr@gmail.com"
                    className="font-display text-2xl md:text-3xl hover:text-[var(--pink)] transition-colors inline-flex items-center gap-2"
                  >
                    medhealthgr@gmail.com <ArrowUpRight className="w-5 h-5" />
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-10 h-10 border border-[var(--ink)] flex items-center justify-center bg-[var(--bg)]">
                  <MapPin className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-mono-caps text-[10px] opacity-60 mb-1">ΤΟΠΟΘΕΣΙΑ</div>
                  <div className="font-display text-2xl md:text-3xl">Αθήνα, GR</div>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-10 h-10 border border-[var(--ink)] flex items-center justify-center bg-[var(--bg)]">
                  <Instagram className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-mono-caps text-[10px] opacity-60 mb-1">SOCIAL</div>
                  <div className="font-display text-2xl md:text-3xl">@blouzes4all</div>
                </div>
              </li>
            </ul>
          </motion.aside>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            onSubmit={buildMailto}
            className="md:col-span-7 border border-[var(--ink)] p-6 md:p-10 bg-[var(--bg-2)]"
          >
            <div className="space-y-6">
              <div>
                <label htmlFor="contact-name" className="font-mono-caps text-[10px] block mb-2">
                  ΟΝΟΜΑ
                </label>
                <input
                  id="contact-name"
                  data-testid={CONTACT.name}
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  type="text"
                  placeholder="Πώς σε λένε;"
                  className="w-full bg-transparent border-b border-[var(--ink)] py-3 font-display text-xl md:text-2xl focus:outline-none focus:border-b-2 placeholder:text-[var(--ink-muted)]"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="font-mono-caps text-[10px] block mb-2">
                  EMAIL
                </label>
                <input
                  id="contact-email"
                  data-testid={CONTACT.email}
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  type="email"
                  placeholder="your@email.com"
                  className="w-full bg-transparent border-b border-[var(--ink)] py-3 font-display text-xl md:text-2xl focus:outline-none focus:border-b-2 placeholder:text-[var(--ink-muted)]"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="font-mono-caps text-[10px] block mb-2">
                  ΜΗΝΥΜΑ
                </label>
                <textarea
                  id="contact-message"
                  data-testid={CONTACT.message}
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Πες μας τι έχεις στο μυαλό σου…"
                  className="w-full bg-transparent border border-[var(--ink)] p-4 text-base md:text-lg focus:outline-none focus:ring-2 focus:ring-[var(--ink)] resize-none placeholder:text-[var(--ink-muted)]"
                />
              </div>
              <button
                data-testid={CONTACT.submit}
                type="submit"
                className="btn-lime"
              >
                ΑΠΟΣΤΟΛΗ ΜΗΝΥΜΑΤΟΣ <Send className="w-4 h-4" />
              </button>
              <p className="text-[11px] text-[var(--ink-muted)]">
                Το κουμπί ανοίγει την εφαρμογή email σου με προσυμπληρωμένο το
                μήνυμα προς το medhealthgr@gmail.com.
              </p>
            </div>
          </motion.form>
        </div>
      </main>
      <Footer />
    </div>
  );
}
